"use client";

import { Code2, Eye } from "lucide-react";

import { cn } from "@/lib/utils";
import { t } from "@/lib/localization";
import { HtmlBlock } from "@/lib/utils/lomdaV3/types";
import { useLomdaV3Editor } from "../../store/useLomdaV3Editor";
import { ToolbarDivider, WidthControl } from "./blockAppearance";

const SEGMENTS = [
    { value: false, icon: Code2, labelKey: "lomda-v3-editor.html.code" },
    { value: true, icon: Eye, labelKey: "lomda-v3-editor.html.preview" },
] as const;

/**
 * Html topbar: Width + a Code / Preview toggle. The toggle used to sit inside
 * the block; it lives here so every block is driven from the same place.
 * `htmlPreview` is transient store state — a view toggle must never persist
 * into the saved lomda.
 */
export function HtmlSettings({ block }: { block: HtmlBlock }) {
    const preview = useLomdaV3Editor((s) => s.htmlPreview[block.id] ?? false);
    const setHtmlPreview = useLomdaV3Editor((s) => s.setHtmlPreview);

    return (
        <div className="flex items-center gap-3">
            <WidthControl blockId={block.id} />
            <ToolbarDivider />
            {/* Segmented control: Code and Preview are two states of ONE setting,
                so they share a single bordered container rather than reading as
                two independent buttons. Selected = Hover fill. */}
            <div className="flex h-9 shrink-0 items-center gap-1 rounded-lg border border-border bg-card p-1">
                {SEGMENTS.map(({ value, icon: Icon, labelKey }) => {
                    const selected = preview === value;
                    return (
                        <button
                            key={labelKey}
                            type="button"
                            onMouseDown={(e) => e.preventDefault()}
                            onClick={() => setHtmlPreview(block.id, value)}
                            className={cn(
                                "text-sm flex h-full items-center gap-2 rounded-lg px-2.5 transition",
                                selected
                                    ? "bg-muted text-foreground"
                                    : "text-muted-foreground hover:text-foreground"
                            )}
                        >
                            <Icon className="size-5" />
                            {t(labelKey)}
                        </button>
                    );
                })}
            </div>
        </div>
    );
}
